/**
 * Internal dependencies
 */
export type DeleteUserListParams = {
	listId: string;
};

/**
 * Deletes a user list by its ID in the Valio API.
 * @link https://evermade.stoplight.io/docs/valio-app/h8rsp4a9ynrcb-delete-users-user-id-lists-list-id
 * @param {Params} params - The parameters for deleting the user list.
 * @param {string} params.listId - The ID of the list to delete.
 * @return {Promise<boolean>} A promise that resolves when the user list is deleted.
 */
export async function deleteUserList(params: DeleteUserListParams): Promise<boolean> {
	const { host } = window.valioConfig;
	const { session } = window.valioApp;

	// Validate required params
	if (!params.listId) {
		throw new Error('List ID is required to delete a user list.');
	}

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot create list.'));
	}

	const headers = await session.authHeader();

	// Make the API request to create the list
	return fetch(`${host}/api/v1/users/${session.userId}/lists/${params.listId}`, {
		method: 'DELETE',
		headers,
	})
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			// Successfully deleted the list
			return true;
		})
		.catch((error) => {
			console.error('Error deleting user list:', error);
			throw error;
		});
}
